import type { ApiHandlerContract } from '@sparklayer-io/spark-dash-core/types/handlers/api';
import { registerResettable } from './reset';
import { type AuthStore, createAuthStore } from './session-auth';
import { createEnvStore, type EnvStore } from './session-env';
import { createSettingsStore, type SettingsStore } from './session-settings';

export type { AuthState, AuthStore } from './session-auth';
export type { EnvState, EnvStore } from './session-env';
export { defaultSiteEnv, type EnvFallback } from './session-env';
export type { SettingsState, SettingsStore } from './session-settings';
export type { HostAvailableTeam, HostTeam, HostUser } from './session-types';

export type SessionStores = {
  useAuthStore: AuthStore;
  useEnvStore: EnvStore;
  useSettingsStore: SettingsStore;
};

/**
 * Builds the auth/env/settings stores together, wired to the host's `apiHandler` - the composition
 * root a host calls once (`src/lib/session.ts` for spark-dash) and threads the three hooks through
 * from there. The stores reference each other (env clears settings on a failed load and reloads
 * them on a ready one; auth clears both on login/logout; settings reads the current env), so each
 * factory is handed the shared `stores` object to reach the others at call time.
 */
export function createSessionStores(apiHandler: ApiHandlerContract): SessionStores {
  const stores = {} as SessionStores;

  stores.useSettingsStore = createSettingsStore(apiHandler, stores);
  stores.useEnvStore = createEnvStore(apiHandler, stores);
  stores.useAuthStore = createAuthStore(apiHandler, stores);

  registerResettable(stores.useSettingsStore);
  registerResettable(stores.useEnvStore);
  registerResettable(stores.useAuthStore);

  return stores;
}
